import type { Brand, Region } from '@/types/constants';
import { getConfig } from '@/utils/config/config';
import { PRODUCT } from '@/utils/constants';
import { getRewardsApiUrlFromAppConfig } from '@/logic/rewards-hub/utils/get-rewards-api-url';
import { buildUrl } from '@/utils/url/build-url';
import { fetchApi } from './fetcher';

const POKER_TOURNAMENT_TICKET_MODULE = 'poker_tournament_ticket';
const PLAYER_NOT_FOUND_ERROR_CODE = 'ERR_PLAYER_NOT_FOUND';

type PokerTournamentTicketsEnvelope = {
    pokerTournamentTickets?: {
        success?: boolean;
        error?: {
            message?: string;
        };
    };
};

export type PlaytechEnrollmentResult = {
    enrolled: boolean;
};

export type CheckPlaytechEnrollmentParams = {
    region: Region | string;
    brand: Brand | string;
};

export const checkPlaytechEnrollment = async ({
    region,
    brand
}: CheckPlaytechEnrollmentParams): Promise<PlaytechEnrollmentResult> => {
    const config = getConfig();
    const rewardsApiUrl = getRewardsApiUrlFromAppConfig(config);

    if (!rewardsApiUrl) {
        return { enrolled: true };
    }

    const url = buildUrl(`${rewardsApiUrl}/components`, {
        product: PRODUCT.POKER,
        'modules[]': POKER_TOURNAMENT_TICKET_MODULE
    });

    const networkConfig = config.network?.InternalLoyaltyRewardsClient;
    const response = await fetchApi(
        url,
        {
            method: 'GET',
            headers: {
                'x-region-code': region,
                'x-brand': brand
            }
        },
        networkConfig ? { networkConfig } : undefined
    );

    if (response.status === 400) {
        return { enrolled: false };
    }

    if (!response.ok) {
        throw new Error(`Playtech enrollment check failed with status ${response.status}`);
    }

    const body = (await response.json().catch(() => null)) as PokerTournamentTicketsEnvelope | null;
    const tickets = body?.pokerTournamentTickets;
    if (tickets?.success === false) {
        const errorMessage = tickets.error?.message ?? '';
        if (errorMessage.includes(PLAYER_NOT_FOUND_ERROR_CODE)) {
            return { enrolled: false };
        }
    }

    return { enrolled: true };
};
